import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function formatCurrency(
	amount: number,
	currency: string = "USD",
): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency,
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount / 100); // Convert from cents
}

export function formatBytes(bytes: number): string {
	if (bytes === 0) return "0 B";

	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}

export function formatNumber(num: number): string {
	return new Intl.NumberFormat("en-US").format(num);
}

export function formatDate(date: string | Date): string {
	return new Intl.DateTimeFormat("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	}).format(new Date(date));
}

export function formatDateTime(date: string | Date): string {
	return new Intl.DateTimeFormat("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	}).format(new Date(date));
}

export function getInitials(name: string): string {
	return name
		.split(" ")
		.map((word) => word[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);
}

export function truncate(str: string, length: number): string {
	if (str.length <= length) return str;
	return `${str.slice(0, length)}...`;
}

/**
 * Security utilities
 */

/**
 * Check if a URL is safe for navigation
 */
export function isSafeUrl(url: string): boolean {
	try {
		const parsedUrl = new URL(url);
		const allowedProtocols = ["http:", "https:"];
		return allowedProtocols.includes(parsedUrl.protocol);
	} catch {
		return false;
	}
}

/**
 * Sanitize HTML string by removing scripts and dangerous attributes
 */
export function sanitizeHtml(html: string): string {
	// Create a temporary div to parse the HTML
	const temp = document.createElement("div");
	temp.innerHTML = html;

	// Remove script tags
	const scripts = temp.querySelectorAll("script");
	scripts.forEach((script) => script.remove());

	// Remove dangerous attributes
	const allElements = temp.querySelectorAll("*");
	allElements.forEach((element) => {
		const dangerousAttrs = [
			"onload",
			"onerror",
			"onclick",
			"onmouseover",
			"onfocus",
			"onblur",
		];
		dangerousAttrs.forEach((attr) => {
			element.removeAttribute(attr);
		});
	});

	return temp.innerHTML;
}

/**
 * Create a secure random string for tokens/IDs
 */
export function createSecureId(length: number = 32): string {
	const array = new Uint8Array(length);
	crypto.getRandomValues(array);
	return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
		"",
	);
}

/**
 * Hash string using Web Crypto API (for client-side hashing)
 */
export async function hashString(str: string): Promise<string> {
	const encoder = new TextEncoder();
	const data = encoder.encode(str);
	const hashBuffer = await crypto.subtle.digest("SHA-256", data);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Debounce function to limit rapid function calls
 */
export function debounce<T extends (...args: any[]) => void>(
	func: T,
	wait: number,
): (...args: Parameters<T>) => void {
	let timeout: NodeJS.Timeout | undefined;

	return (...args: Parameters<T>) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => func(...args), wait);
	};
}
